import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/es";
import "dayjs/locale/fr";
import "dayjs/locale/it";
import { getLanguage, getLanguageId } from "./lang";
import { getLocale } from "@/lang/index";
import POSConfigModule from "@/store/modules/pos-config";
import { dayjs as eldayjs } from "element-plus";

/**
 * format datetime according to b2b account format
 * @param date: string of the date to be formatted
 * @param onlyDate?: whether to return only the date without time
 * @returns string
 */
export function formatDateTime(date: string, onlyDate = false): string {
  const format =
    getGroupDayJsDateFormat() +
    (!onlyDate ? " " + getGroupDayJsTimeFormat() : "");
  return dayjs(date)
    .format(format)
    .replace(/\b(?!am|pm)\w/g, (c) => c.toUpperCase()); // to capitalize the first letter of the month and day names
}

/**
 * do same job as function formatDateTime but fixes a bug of wromg time for orders table
 * @param date: string of the date to be formatted
 * @param onlyDate?: whether to return only the date without time
 * @returns string
 */
export function formatDateTimeOrders(date: string, onlyDate = false): string {
  const format =
    getGroupDayJsDateFormat() +
    (!onlyDate ? " " + getGroupDayJsTimeFormat() : "");
  return dayjs(date)
    .utc()
    .format(format)
    .replace(/\b(?!am|pm)\w/g, (c) => c.toUpperCase());
}

/**
 *
 * @param date Takes a string representing a UTC date value (e.g: timeslots in DB) and returns the UTC date string
 * @returns
 */

export function convertDateToUTC(date: string): string {
  const d = new Date(date);
  const utc = Date.UTC(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes(),
    d.getSeconds()
  );

  return new Date(utc).toUTCString();
}

/**
 * Takes a date in a string format and returns it as 31 Jan. 2020 13:00
 */
export function formattedDate(date: string, hasAt?: boolean): string {
  // const d = new Date(date);
  const d = dayjs(date);
  // if (d) {
  //   return (
  //     d.getDate() +
  //     " " +
  //     d.toLocaleString("default", { month: "short" }) +
  //     ". " +
  //     d.getFullYear() +
  //     " " +
  //     (hasAt ? " at " : "") +
  //     (d.getHours() > 9 ? d.getHours() : "0" + d.getHours().toString()) +
  //     ":" +
  //     (d.getMinutes() > 9 ? d.getMinutes() : "0" + d.getMinutes().toString())
  //   );
  // }
  if (d) {
    return d.format("DD MMM YYYY HH:mm");
  }
  return date;
}

/**
 * Takes a date in a string format with utc date value, create a utc date object out of it,
 *  and then convert to the corresponding local time
 */
export function convertDateToLocale(date: string) {
  if (!date) {
    return;
  }
  const utcDate = convertDateToUTC(date);
  const localeDate = new Date(utcDate).toLocaleString();
  return localeDate;
}

/**
 * Changes locale for certain data, locally
 */
export function formatAndConvertToLocale(date: string) {
  return dayjs(date).locale(getLocale()).format("DD MMM YYYY HH:mm");
}

/**
 * Returns current time as ISO string
 */
export function nowDB() {
  return dayjs().toISOString();
}

/**
 * return dayjs date format parsed from b2b date format
 */
export function getGroupDayJsDateFormat(): string {
  const groupDateFormat = POSConfigModule.groupInfo?.dateFormatB2b || "";
  if (!groupDateFormat) return "D MMM, YYYY";
  const dayjsFormat = groupDateFormat
    .replace(/\bD\b/g, "dd")
    .replace(/\bj\b|\bd\b/g, "DD")
    .replace(/\bM\b/g, "MMM")
    .replace(/\bY\b/g, "YYYY")
    .replace(/\bm\b/g, "MM");
  return dayjsFormat;
}

/**
 * return dayjs time format parsed from b2b time format
 */
export function getGroupDayJsTimeFormat(): string {
  const groupTimeFormat = POSConfigModule.groupInfo?.timeFormatB2b || "";
  if (!groupTimeFormat) return "HH:mm";
  const timeFormat = !groupTimeFormat ? "HH:mm" : "hh:mm a";
  return timeFormat;
}

/**
 * sets element-plus week start from b2b settings
 */
export function setElementPlusWeekStart(lang?: string) {
  if (!lang) {
    lang = getLanguage();
  }
  const weekStart = POSConfigModule.groupInfo?.weekStartB2b || undefined;
  (eldayjs as any).Ls[getLanguage()] ??= {};
  eldayjs.Ls[getLanguage()].weekStart = weekStart;
}

/**
 * converts date time string to a week day name
 * @param date date time string
 */
export function dateTimeToWeekDay(date: string) {
  return dayjs(date)
    .toDate()
    .toLocaleDateString("en", { weekday: "long" })
    .toLowerCase();
}

/**
 * Gets the closest date from an array of dates
 * @param dates array of dates
 * @param target target date
 */
export function getClosestDate(dates: Dayjs[], target: Dayjs) {
  return dates.reduce((closest, current) => {
    const currentDiff = Math.abs(dayjs(current).diff(target));
    const closestDiff = Math.abs(dayjs(closest).diff(target));
    return currentDiff < closestDiff ? current : closest;
  });
}
